import { Box } from '@mui/material';
import { Formik, Form } from 'formik';
import { useMemo } from 'react';

import { ResidencyStatusField } from './commonField';
import FormSkeleton from './formSkeleton';
import FormField from 'components/common/formikForm/formField';
import { FormBusyProvider } from 'components/common/formikForm/formBusyContext';
import FormGridLayout from 'components/common/formikForm/formGridLayout';
import { StepNavigation } from 'components/v2/StepNavigation';

import { genderList } from 'constant';
import {
    useCountries,
    useNationalities,
    useRegions,
    useSpouseInfo,
    useSaveSpouseInfo,
} from 'hooks';
import useTranslate from 'hooks/useTranslate';
import type { SpouseInfo } from 'types';
import {
    formatDayjsDate,
    generateOptionsFromArray,
    generateOptionsFromString,
} from 'utils/dataFormatter';
import { isDestinationCountry } from 'utils/countries';
import { spouseValidationSchema } from 'utils/validations/spouseSchema';

interface SpouseInfoStepProps {
    isLastStep: boolean;
    onNext: (data: Record<string, unknown>) => void;
    onBack: () => void;
}

const emptySpouse: Partial<SpouseInfo> = {
    firstname: '',
    lastname: '',
    middlename: '',
    birth_date: '',
    gender: '',
    nationality: '',
    country_of_residence: '',
    region_of_residence: '',
    residency_status: '',
    residence_since: '',
    income: 0,
};

const SpouseInfoStep = ({ isLastStep, onNext, onBack }: SpouseInfoStepProps) => {
    const { translate } = useTranslate();
    const translatedGenderOptions = useMemo(() => {
        return genderList.map((value) => translate(value));
    }, [translate]);

    const genderOptions = generateOptionsFromArray(translatedGenderOptions);

    const { data: apiData, isLoading } = useSpouseInfo();
    const { data: countries } = useCountries();
    const { data: nationalities } = useNationalities();
    const { data: regions } = useRegions();
    const saveMutation = useSaveSpouseInfo();

    const initialValues: Partial<SpouseInfo> = {
        ...emptySpouse,
        ...(apiData || {}),
        firstname: apiData?.firstname ?? '',
        lastname: apiData?.lastname ?? '',
        middlename: apiData?.middlename ?? '',
        birth_date: apiData?.birth_date ?? '',
        gender: apiData?.gender ?? '',
        nationality: apiData?.nationality ?? '',
        country_of_residence: apiData?.country_of_residence ?? '',
        region_of_residence: apiData?.region_of_residence ?? '',
        residency_status: apiData?.residency_status ?? '',
        residence_since: apiData?.residence_since ?? '',
        income: apiData?.income ?? 0,
    };

    const handleSubmit = async (values: SpouseInfo) => {
        try {
            // Drop destination-only fields when not applicable so payload stays clean
            const payload: SpouseInfo = { ...values };
            if (!isDestinationCountry(values.country_of_residence)) {
                payload.region_of_residence = '';
                payload.residency_status = '';
            }
            await saveMutation.mutateAsync(payload);
            onNext(payload as unknown as Record<string, unknown>);
        } catch {
            // Error alert is handled by the mutation hook onError
        }
    };

    if (isLoading) {
        return <FormSkeleton fields={8} />;
    }

    const countryOptions = generateOptionsFromString(countries ?? []);
    const nationalityOptions = generateOptionsFromString(nationalities ?? []);

    return (
        <Box>
            <Formik
                enableReinitialize
                initialValues={initialValues as SpouseInfo}
                validationSchema={spouseValidationSchema}
                onSubmit={handleSubmit}
            >
                {({ isValid, isSubmitting, values, setFieldValue }) => {
                    const showDestinationFields = isDestinationCountry(values.country_of_residence);
                    const regionOptions = generateOptionsFromString(
                        showDestinationFields && values.country_of_residence
                            ? (regions as Record<string, string[]> | null)?.[
                                  values.country_of_residence
                              ] || []
                            : []
                    );

                    const isSaving = isSubmitting || saveMutation.isPending;

                    return (
                        <FormBusyProvider busy={isSaving}>
                            <Form>
                                <FormGridLayout>
                                    <FormField required name="firstname" label="firstName" />
                                    <FormField name="middlename" label="middleName" />
                                    <FormField required name="lastname" label="lastName" />

                                    <FormField
                                        type="radio"
                                        name="gender"
                                        label="gender"
                                        options={genderOptions}
                                    />
                                    <FormField
                                        required
                                        type="date"
                                        name="birth_date"
                                        label="dateOfBirth"
                                        maxDaysFromToday
                                    />

                                    <FormField
                                        required
                                        type="autosuggest"
                                        name="nationality"
                                        label="nationality"
                                        options={nationalityOptions}
                                    />
                                    <FormField
                                        required
                                        type="autosuggest"
                                        name="country_of_residence"
                                        label="countryOfResidence"
                                        options={countryOptions}
                                        onChange={(value) => {
                                            setFieldValue('country_of_residence', value);
                                            // Reset destination-only fields (AssessmentV2 SpousePartnerInfo)
                                            setFieldValue('region_of_residence', '');
                                            setFieldValue('residency_status', '');
                                        }}
                                    />

                                    {showDestinationFields && (
                                        <>
                                            <FormField
                                                required
                                                type="autosuggest"
                                                name="region_of_residence"
                                                label="regionOfResidence"
                                                options={regionOptions}
                                                disabled={!values.country_of_residence}
                                            />
                                            <ResidencyStatusField
                                                required
                                                countryOfResidence={
                                                    values.country_of_residence || ''
                                                }
                                                name="residency_status"
                                                label="statusInCountryOfResidence"
                                            />
                                        </>
                                    )}

                                    <FormField
                                        type="monthyear"
                                        minDate={formatDayjsDate(values.birth_date)}
                                        name="residence_since"
                                        label="residenceStatusSince"
                                    />
                                    <FormField
                                        name="income"
                                        label="approxYearlyIncome(USD)"
                                        type="number"
                                    />
                                </FormGridLayout>

                                <StepNavigation
                                    isLastStep={isLastStep}
                                    onBack={onBack}
                                    isSubmitting={isSaving}
                                    disableNext={!isValid}
                                />
                            </Form>
                        </FormBusyProvider>
                    );
                }}
            </Formik>
        </Box>
    );
};

export default SpouseInfoStep;
